import { apiClient } from './api';

// EG-52 — Platform Admin quản lý ngân hàng cross-tenant.
// BE endpoint: /banks (CRUD).

export interface BankFilters {
  page?: number;
  limit?: number;
  tenant_id?: string;
  q?: string;
}

export interface Bank {
  bank_id: string;
  tenant_id?: string;
  name: string;
  bin: string;
  code: string;
  logo: string;
  short_name: string;
  swift_code?: string;
  ordering: number;
}

export interface CreateBankDto {
  tenant_id: string;
  name: string;
  bin: string;
  code: string;
  logo: string;
  short_name: string;
  swift_code?: string;
  ordering?: number;
}

export interface UpdateBankDto {
  name?: string;
  bin?: string;
  code?: string;
  logo?: string;
  short_name?: string;
  swift_code?: string;
  ordering?: number;
}

export interface BanksResponse {
  data: Bank[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

interface BankApiRaw {
  bank_id?: string;
  _id?: string;
  tenant_id?: string;
  name?: string;
  bin?: string;
  code?: string;
  logo?: string;
  short_name?: string;
  swift_code?: string;
  ordering?: number;
}

const toBank = (raw: BankApiRaw): Bank => ({
  bank_id: raw.bank_id || raw._id || '',
  tenant_id: raw.tenant_id,
  name: raw.name || '',
  bin: raw.bin || '',
  code: raw.code || '',
  logo: raw.logo || '',
  short_name: raw.short_name || '',
  swift_code: raw.swift_code,
  ordering: raw.ordering ?? 0,
});

const unwrap = <T>(payload: Envelope<T> | T): T => {
  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    return (payload as Envelope<T>).data;
  }
  return payload as T;
};

export const bankService = {
  getBanks: async (filters: BankFilters = {}): Promise<BanksResponse> => {
    const response = await apiClient.get<Envelope<{ items: BankApiRaw[]; meta: BanksResponse['meta'] }>>(
      '/banks',
      { params: filters },
    );
    const payload = response.data?.data;
    return {
      data: (payload?.items || []).map(toBank),
      meta: payload?.meta || {
        page: filters.page || 1,
        limit: filters.limit || 20,
        total: 0,
        totalPages: 0,
      },
    };
  },

  getBankById: async (id: string): Promise<Bank> => {
    const response = await apiClient.get<Envelope<BankApiRaw>>(`/banks/${id}`);
    return toBank(response.data.data);
  },

  createBank: async (data: CreateBankDto): Promise<Bank> => {
    const response = await apiClient.post<Envelope<BankApiRaw> | BankApiRaw>('/banks', data);
    return toBank(unwrap(response.data));
  },

  updateBank: async (id: string, data: UpdateBankDto): Promise<Bank> => {
    const response = await apiClient.put<Envelope<BankApiRaw> | BankApiRaw>(`/banks/${id}`, data);
    return toBank(unwrap(response.data));
  },

  deleteBank: async (id: string): Promise<void> => {
    await apiClient.delete(`/banks/${id}`);
  },
};

export default bankService;
